import React, { useState, useEffect, useRef } from "react";
import { Result } from "../../types";
import PDFExport from "./CRMPDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Radar } from "react-chartjs-2";
import {
  Groups,
  TrendingUp,
  Assessment,
  Business,
  Smartphone,
  Email,
  Analytics,
  Settings,
  Celebration,
  Assignment,
  Speed,
  AttachMoney,
  GpsFixed,
  Timeline,
} from "@mui/icons-material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface ResultsPageProps {
  results: Result[];
  onRestart: () => void;
}

const ResultsPage: React.FC<ResultsPageProps> = ({ results, onRestart }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("recommendations");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Enhanced CRM recommendations with MUI icons
  const generateDetailedRecommendations = () => {
    const recommendations = [];
    const answers = results.map((r) => r.answer.toLowerCase());
    const businessSize = answers.find(
      (a) => a.includes("small") || a.includes("medium") || a.includes("large")
    );
    const hasEmailNeeds = answers.some(
      (a) => a.includes("email") || a.includes("communication")
    );
    const hasSalesNeeds = answers.some(
      (a) => a.includes("sales") || a.includes("lead")
    );
    const hasAnalyticsNeeds = answers.some(
      (a) => a.includes("report") || a.includes("analytic")
    );

    // Core CRM Features
    recommendations.push({
      category: "Core Features",
      title: "Contact Management System",
      description:
        "Centralized customer database with comprehensive contact profiles, interaction history, and relationship mapping.",
      priority: "High",
      icon: <Groups />,
      benefits: [
        "360° customer view",
        "Contact segmentation",
        "Interaction tracking",
        "Data synchronization",
      ],
      implementation: "4-6 weeks",
      roi: "150% within 6 months",
      score: 95,
    });

    if (hasSalesNeeds) {
      recommendations.push({
        category: "Sales & Pipeline",
        title: "Sales Pipeline Management",
        description:
          "Visual sales funnel with deal tracking, forecasting, and automated workflow management.",
        priority: "High",
        icon: <TrendingUp />,
        benefits: [
          "Visual deal tracking",
          "Sales forecasting",
          "Automated workflows",
          "Performance analytics",
        ],
        implementation: "6-8 weeks",
        roi: "200% within 4 months",
        score: 92,
      });

      recommendations.push({
        category: "Sales & Pipeline",
        title: "Lead Scoring & Management",
        description:
          "Intelligent lead qualification and scoring system with automated nurturing campaigns.",
        priority: "High",
        icon: <GpsFixed />,
        benefits: [
          "Automated lead scoring",
          "Qualification workflows",
          "Nurturing campaigns",
          "Conversion tracking",
        ],
        implementation: "8-10 weeks",
        roi: "180% within 5 months",
        score: 88,
      });
    }

    if (hasEmailNeeds) {
      recommendations.push({
        category: "Communication",
        title: "Email Integration & Automation",
        description:
          "Seamless email integration with automated campaigns, templates, and response tracking.",
        priority: "Medium",
        icon: <Email />,
        benefits: [
          "Email synchronization",
          "Automated campaigns",
          "Template library",
          "Response tracking",
        ],
        implementation: "4-6 weeks",
        roi: "120% within 6 months",
        score: 85,
      });
    }

    if (hasAnalyticsNeeds) {
      recommendations.push({
        category: "Analytics & Reporting",
        title: "Advanced Analytics Dashboard",
        description:
          "Comprehensive reporting suite with real-time dashboards, custom reports, and predictive analytics.",
        priority: "Medium",
        icon: <Analytics />,
        benefits: [
          "Real-time dashboards",
          "Custom reporting",
          "Predictive analytics",
          "Data visualization",
        ],
        implementation: "3-4 weeks",
        roi: "160% within 8 months",
        score: 82,
      });
    }

    // Additional features based on business size
    if (businessSize?.includes("large")) {
      recommendations.push({
        category: "Enterprise Features",
        title: "Advanced Workflow Automation",
        description:
          "Complex business process automation with conditional logic and multi-step workflows.",
        priority: "Medium",
        icon: <Settings />,
        benefits: [
          "Process automation",
          "Conditional workflows",
          "Task management",
          "Approval processes",
        ],
        implementation: "6-8 weeks",
        roi: "140% within 10 months",
        score: 78,
      });
    }

    recommendations.push({
      category: "Mobility",
      title: "Mobile CRM Application",
      description:
        "Full-featured mobile app for on-the-go access to customer data and sales activities.",
      priority: "Low",
      icon: <Smartphone />,
      benefits: [
        "Mobile access",
        "Offline capabilities",
        "Real-time sync",
        "Location tracking",
      ],
      implementation: "4-5 weeks",
      roi: "110% within 12 months",
      score: 75,
    });

    return recommendations;
  };

  const recommendations = generateDetailedRecommendations();
  const categories = [...new Set(recommendations.map((r) => r.category))];

  // Remove this unused priorityData variable (around line 237):
  // const priorityData = {
  //   labels: [
  //     "High Priority (Immediate Implementation)",
  //     "Medium Priority (Phase 2)",
  //     "Low Priority (Future Consideration)",
  //   ],
  //   datasets: [
  //     {
  //       data: [
  //         recommendations.filter((r) => r.priority === "High").length,
  //         recommendations.filter((r) => r.priority === "Medium").length,
  //         recommendations.filter((r) => r.priority === "Low").length,
  //       ],
  //       backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
  //       borderWidth: 0,
  //       hoverOffset: 10,
  //     },
  //   ],
  // };

  // Keep the other chart data objects that are actually being used:
  const categoryData = {
    labels: categories,
    datasets: [
      {
        label: "Number of Recommended Features",
        data: categories.map(
          (cat) => recommendations.filter((r) => r.category === cat).length
        ),
        backgroundColor: "rgba(99, 102, 241, 0.8)",
        borderColor: "#6366f1",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const businessImpactData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Business Impact Score (0-100)",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.3)",
        borderColor: "#10b981",
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#10b981",
      },
    ],
  };

  const roiTimelineData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Expected ROI (%)",
        data: recommendations.map((r) => {
          // Extract ROI percentage from strings like "150% within 6 months"
          const roiMatch = r.roi.match(/(\d+)%/);
          return roiMatch ? parseInt(roiMatch[1]) : 100;
        }),
        backgroundColor: "rgba(245, 158, 11, 0.6)",
        borderColor: "#f59e0b",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Implementation Score",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.6)",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Priority distribution based on actual user responses
  const responseBasedPriority = {
    labels: [
      "Immediate Impact (High ROI)",
      "Strategic Growth (Medium ROI)",
      "Future Enhancement (Lower ROI)",
    ],
    datasets: [
      {
        data: [
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 120 && roiValue < 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue < 120;
          }).length,
        ],
        backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header with responsive design */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "2.5rem" : "4rem",
              marginBottom: "1rem",
            }}
          >
            <Celebration
              sx={{ fontSize: isMobile ? "2.5rem" : "4rem", color: "white" }}
            />
          </div>
          <h1
            style={{
              fontFamily: "var(--font-headline)",
              fontSize: isMobile
                ? "var(--font-size-headline-mobile)"
                : "var(--font-size-headline)",
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(35deg,rgb(215, 227, 255) 0%,rgb(155, 188, 255) 30%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Your Personalized CRM Roadmap
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: isMobile
                ? "var(--font-size-body-mobile)"
                : "var(--font-size-body)",
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Based on your responses, we've created a comprehensive CRM strategy
            tailored to your business needs with detailed implementation
            guidance and ROI projections.
          </p>

          {/* Key Statistics - Responsive Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isMobile ? "0.75rem" : "1rem",
              maxWidth: "800px",
              margin: "0 auto",
              padding: isMobile ? "0 0.5rem" : "0",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Assignment
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                {recommendations.length}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                CRM Features
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Speed
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                4-10 weeks
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Implementation
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                gridColumn: isMobile ? "1 / -1" : "auto",
              }}
            >
              <AttachMoney
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                150%+
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Expected ROI within 6-12 months
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Responsive */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            gap: isMobile ? "0.5rem" : "0",
          }}
        >
          {["recommendations", "analytics", "your-responses"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: isMobile ? "0.75rem 1rem" : "1rem 2rem",
                margin: isMobile ? "0" : "0 0.5rem",
                backgroundColor:
                  selectedTab === tab
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.1)",
                color: selectedTab === tab ? "#1f2937" : "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                textTransform: "capitalize",
                minWidth: isMobile ? "auto" : "120px",
              }}
            >
              {tab === "your-responses" ? "Your Responses" : tab}
            </button>
          ))}
        </div>

        {/* Content based on selected tab */}
        {selectedTab === "recommendations" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Detailed Recommendations - Responsive Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: isMobile ? "1.5rem" : "2.5rem",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "transform 0.3s ease",
                    transitionDelay: `${index * 0.1}s`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(-8px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {/* Priority Badge - Responsive */}
                  <div
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      padding: isMobile ? "0.3rem 0.75rem" : "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: isMobile ? "0.7rem" : "0.8rem",
                      fontWeight: "600",
                      backgroundColor:
                        recommendation.priority === "High"
                          ? "#fee2e2"
                          : recommendation.priority === "Medium"
                          ? "#fef3c7"
                          : "#d1fae5",
                      color:
                        recommendation.priority === "High"
                          ? "#dc2626"
                          : recommendation.priority === "Medium"
                          ? "#d97706"
                          : "#059669",
                    }}
                  >
                    {recommendation.priority} Priority
                  </div>

                  {/* Header - Responsive */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "2rem" : "3rem",
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "1rem" : "0",
                        color: "#c9a750ff",
                      }}
                    >
                      {React.cloneElement(recommendation.icon, {
                        sx: { fontSize: isMobile ? "2rem" : "3rem" },
                      })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#c9a750ff",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {recommendation.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {recommendation.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}
                  >
                    {recommendation.description}
                  </p>

                  {/* Benefits - Responsive Grid */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: "1rem",
                      }}
                    >
                      Key Benefits:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(2, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {recommendation.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                            color: "#4b5563",
                          }}
                        >
                          <span
                            style={{ color: "#10b981", marginRight: "0.5rem" }}
                          >
                            ✓
                          </span>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Details - Responsive */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "1rem",
                      padding: "1rem",
                      backgroundColor: "rgba(201, 167, 80, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Timeline
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {recommendation.implementation}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Expected ROI
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#059669",
                        }}
                      >
                        {recommendation.roi}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Impact Score
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#c9a750ff",
                        }}
                      >
                        {recommendation.score}/100
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === "analytics" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Charts Grid - Responsive */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {/* Chart containers with responsive padding */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <GpsFixed
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  ROI-Based Priority
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Feature prioritization based on expected return on investment
                  for your specific needs
                </p>
                <Doughnut
                  data={responseBasedPriority}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 20,
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.label}: ${context.parsed} features`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Repeat similar responsive styling for other charts */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Assessment
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Features by Business Area
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Distribution of recommended CRM features across different
                  business functions
                </p>
                <Bar
                  data={categoryData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.parsed.y} recommended features`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: !isMobile,
                          text: "Number of Features",
                        },
                        ticks: {
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      x: {
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                          maxRotation: isMobile ? 45 : 0,
                        },
                      },
                    },
                  }}
                />
              </div>

              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Business
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Business Impact Score
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Potential business impact of each recommended feature (score
                  out of 100)
                </p>
                <Radar
                  data={businessImpactData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `Impact Score: ${context.parsed.r}/100`;
                          },
                        },
                      },
                    },
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          stepSize: 20,
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                        pointLabels: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Timeline
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  ROI vs Implementation Score
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Expected ROI and implementation difficulty for your
                  recommended features
                </p>
                <Bar
                  data={roiTimelineData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 15,
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            const label = context.dataset.label;
                            const value = context.parsed.y;
                            return `${label}: ${value}${
                              label.includes("ROI") ? "%" : "/100"
                            }`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 200,
                        title: {
                          display: !isMobile,
                          text: "Percentage / Score",
                        },
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                      x: {
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                          maxRotation: isMobile ? 45 : 0,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Chart Explanations - Responsive */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2rem",
                backdropFilter: "blur(10px)",
                marginBottom: "2rem",
              }}
            >
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: "#1f2937",
                  fontSize: isMobile ? "1.2rem" : "1.3rem",
                }}
              >
                📖 Understanding Your CRM Analytics
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    💰 ROI-Based Priority
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>Immediate Impact:</strong> High ROI features (150%+)
                    for quick wins
                    <br />
                    <strong>Strategic Growth:</strong> Medium ROI features
                    (120-149%) for sustained growth
                    <br />
                    <strong>Future Enhancement:</strong> Lower ROI features
                    (&lt;120%) for long-term value
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    📊 ROI vs Implementation
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>Orange bars:</strong> Expected ROI percentage within
                    timeframe
                    <br />
                    <strong>Green bars:</strong> Implementation difficulty score
                    (higher = easier)
                    <br />
                    This helps identify features with best ROI-to-effort ratio
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Your Responses section would follow similar responsive patterns */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Response content with mobile responsiveness */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2.5rem",
                marginBottom: "2rem",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Response Summary Stats - Mobile responsive grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                  padding: isMobile ? "1rem" : "1.5rem",
                  backgroundColor: "rgba(201, 167, 80, 0.1)",
                  borderRadius: "12px",
                }}
              >
                {/* Stats cards with responsive text sizes */}
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#c9a750ff",
                    }}
                  >
                    📊
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {results.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Questions Answered
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#10b981",
                    }}
                  >
                    ⚡
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {
                      recommendations.filter((r) => r.priority === "High")
                        .length
                    }
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    High Priority Features
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#f59e0b",
                    }}
                  >
                    🎯
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {Math.round(
                      recommendations.reduce((acc, r) => acc + r.score, 0) /
                        recommendations.length
                    )}
                    %
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Avg. Match Score
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#8b5cf6",
                    }}
                  >
                    💼
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {categories.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Business Areas Covered
                  </div>
                </div>
              </div>

              {/* Individual responses with mobile optimization */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: isMobile ? "1rem" : "1.5rem",
                }}
              >
                {results.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      padding: isMobile ? "1.5rem" : "2rem",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "16px",
                      border: "1px solid rgba(201, 167, 80, 0.2)",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    {/* Question header with responsive layout */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "1.5rem",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#c9a750ff",
                          color: "white",
                          borderRadius: "50%",
                          width: isMobile ? "28px" : "32px",
                          height: isMobile ? "28px" : "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          fontWeight: "600",
                          marginRight: isMobile ? "0" : "1rem",
                          marginBottom: isMobile ? "1rem" : "0",
                          flexShrink: 0,
                          alignSelf: isMobile ? "center" : "flex-start",
                        }}
                      >
                        Q{index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5
                          style={{
                            fontWeight: "700",
                            color: "#1f2937",
                            marginBottom: "0.5rem",
                            fontSize: isMobile ? "1rem" : "1.1rem",
                            lineHeight: "1.4",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {result.question}
                        </h5>
                      </div>
                    </div>

                    {/* Answer and impact sections with responsive styling */}
                    <div
                      style={{
                        backgroundColor: "rgba(201, 167, 80, 0.1)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #c9a750ff",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Your Response:
                      </div>
                      <div
                        style={{
                          color: "#c9a750ff",
                          fontWeight: "600",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {result.answer}
                      </div>
                    </div>

                    {/* Impact Analysis */}
                    <div
                      style={{
                        backgroundColor: "rgba(16, 185, 129, 0.05)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #10b981",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Impact on Recommendations:
                      </div>
                      <div
                        style={{
                          color: "#059669",
                          fontSize: isMobile ? "0.9rem" : "0.95rem",
                          lineHeight: "1.5",
                        }}
                      >
                        {/* Dynamic impact analysis based on the answer */}
                        {result.answer.toLowerCase().includes("sales") ||
                        result.answer.toLowerCase().includes("lead")
                          ? "✓ Triggered sales-focused CRM features including pipeline management and lead scoring"
                          : result.answer.toLowerCase().includes("email") ||
                            result.answer
                              .toLowerCase()
                              .includes("communication")
                          ? "✓ Recommended email integration and communication automation features"
                          : result.answer.toLowerCase().includes("analytics") ||
                            result.answer.toLowerCase().includes("report")
                          ? "✓ Added advanced analytics and reporting capabilities to your recommendations"
                          : result.answer.toLowerCase().includes("large") ||
                            result.answer.toLowerCase().includes("enterprise")
                          ? "✓ Included enterprise-level features and advanced workflow automation"
                          : result.answer.toLowerCase().includes("small") ||
                            result.answer.toLowerCase().includes("startup")
                          ? "✓ Focused on essential features suitable for smaller teams and budgets"
                          : "✓ Contributed to your personalized CRM feature selection and priority ranking"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Responsive */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "0.75rem" : "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <PDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
